import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Users, Mic, Play, Pause, Search, Calendar, Gift, Download, Key, Filter, Star } from 'lucide-react';
import { RatingDetails } from '../components/community/RatingDetails';
import { CalendarModal } from '../components/community/CalendarModal';
import { formatUnavailabilityBadge } from '../utils/availability';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Community = () => {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === 'dono' || user?.role === 'administrador';
  const [users, setUsers] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notasGerais, setNotasGerais] = useState({});
  const [availFilter, setAvailFilter] = useState(''); // '' | 'disponivel' | 'indisponivel'
  const [dublaIaFilter, setDublaIaFilter] = useState(''); // '' | 'dubla' | 'nao_dubla'
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchUsers(); fetchCalendarEvents(); fetchNotas(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/community/members`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch { toast.error('Erro ao carregar membros'); }
    finally { setLoading(false); }
  };

  const fetchNotas = async () => {
    try {
      const res = await axios.get(`${API}/tools/ratings/notas-gerais`, { headers: getAuthHeaders() });
      setNotasGerais(res.data || {});
    } catch (err) { console.error('Failed to fetch notas:', err); }
  };

  const fetchCalendarEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`, { headers: getAuthHeaders() });
      setCalendarEvents(response.data);
    } catch (err) { console.error('Failed to fetch calendar:', err); }
  };

  const getVoiceUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return process.env.REACT_APP_BACKEND_URL + url;
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePlay = (userId, voiceUrl) => {
    const fullUrl = getVoiceUrl(voiceUrl);
    if (!fullUrl) return;
    if (playingId === userId) {
      stopAudio();
    } else {
      stopAudio();
      const newAudio = new Audio(fullUrl);
      newAudio.onended = () => { setPlayingId(null); audioRef.current = null; };
      newAudio.play().catch(() => { toast.error('Erro ao reproduzir áudio'); setPlayingId(null); });
      audioRef.current = newAudio;
      setPlayingId(userId);
    }
  };

  const closeUserModal = () => {
    stopAudio();
    setSelectedUser(null);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    } catch { return null; }
  };

  const getBirthdayCountdown = (birthDate) => {
    if (!birthDate) return null;
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
      if (nextBirthday < today) nextBirthday = new Date(today.getFullYear() + 1, birth.getMonth(), birth.getDate());
      const diffDays = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays > 0 ? diffDays : null;
    } catch { return null; }
  };

  const handleDownloadVoiceSample = (url, name) => {
    const fullUrl = getVoiceUrl(url);
    if (!fullUrl) return;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = `amostra_${name}.mp3`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const normalizeStr = (s) => s?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w\s]/g, '') || '';

  const filteredUsers = users.filter(u => {
    if (!normalizeStr(u.name).includes(normalizeStr(search)) &&
        !normalizeStr(u.artist_name).includes(normalizeStr(search)) &&
        !normalizeStr(u.role).includes(normalizeStr(search))) return false;
    if (availFilter === 'disponivel' && u.is_available === false) return false;
    if (availFilter === 'indisponivel' && u.is_available !== false) return false;
    if (dublaIaFilter === 'dubla' && !u.dubla_ia) return false;
    if (dublaIaFilter === 'nao_dubla' && u.dubla_ia) return false;
    return true;
  }).sort((a, b) => {
    // Current user always first
    if (a.id === user?.id) return -1;
    if (b.id === user?.id) return 1;
    const nameA = (a.artist_name || a.name || '').toLowerCase();
    const nameB = (b.artist_name || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB, 'pt-BR');
  });

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="community-page">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Users className="h-8 w-8 text-teal-400" />
            Comunidade
          </h1>
          <div className="flex gap-3 items-center flex-wrap">
            {isAdmin && (
              <select value={availFilter} onChange={e => setAvailFilter(e.target.value)}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="availability-filter">
                <option value="">Todos</option>
                <option value="disponivel">Disponíveis</option>
                <option value="indisponivel">Indisponíveis</option>
              </select>
            )}
            {isAdmin && (
              <select value={dublaIaFilter} onChange={e => setDublaIaFilter(e.target.value)}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="dubla-ia-filter"
                title="Filtrar por aceitar dublar projetos com IA">
                <option value="">IA: Todos</option>
                <option value="dubla">Dubla IA</option>
                <option value="nao_dubla">Não dubla IA</option>
              </select>
            )}
            <button onClick={() => setShowCalendar(true)}
              className="flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2 rounded-lg transition-colors"
              data-testid="calendar-btn">
              <Calendar className="h-5 w-5" /> Calendário
            </button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar membro..."
                className="bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-teal-500 w-64"
                data-testid="search-input" />
            </div>
          </div>
        </div>

        {/* Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map(member => {
            const age = calculateAge(member.birth_date);
            const birthdayCountdown = getBirthdayCountdown(member.birth_date);
            return (
              <div key={member.id}
                className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all cursor-pointer"
                onClick={() => setSelectedUser(member)} data-testid={`member-card-${member.id}`}>
                <div className="flex items-start gap-4">
                  {member.picture ? (
                    <img src={member.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 text-2xl font-bold shrink-0">
                      {member.name?.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white truncate">{member.artist_name || member.name}</h3>
                        {isAdmin && member.artist_name && <p className="text-slate-400 text-sm truncate">{member.name}</p>}
                      </div>
                      {birthdayCountdown && (
                        <div className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs flex items-center gap-1 shrink-0">
                          <Gift className="h-3 w-3" />{birthdayCountdown}d
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        member.role === 'dono' ? 'bg-amber-500/20 text-amber-400' :
                        member.role === 'administrador' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-slate-600/50 text-slate-300'
                      }`}>{member.role}</span>
                      {age && <span className="text-slate-400 text-xs">{age} anos</span>}
                      {isAdmin && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${member.emite_nf ? 'bg-teal-500/20 text-teal-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {member.emite_nf ? 'Emite NF' : 'Não emite NF'}
                        </span>
                      )}
                      {isAdmin && (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${member.dubla_ia ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/40 text-slate-400'}`}
                          title={member.dubla_ia ? 'Aceita dublar projetos com conteúdo gerado por IA' : 'Não aceita projetos com IA'}
                          data-testid={`dubla-ia-badge-${member.id}`}
                        >
                          {member.dubla_ia ? 'Dubla IA' : 'Não dubla IA'}
                        </span>
                      )}
                    </div>
                    {/* Nota Geral */}
                    {notasGerais[member.id] !== undefined && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-bold text-yellow-400">{notasGerais[member.id].toFixed(1)}</span>
                        <span className="text-xs text-slate-500">/5</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  {/* Voice categories from the Organizador tool (rating categorias) */}
                  {Array.isArray(member.voice_categories) && member.voice_categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`voice-cats-${member.id}`}>
                      {member.voice_categories.slice(0, 6).map((vc, i) => (
                        <span
                          key={`${member.id}-vc-${i}`}
                          className="px-2 py-0.5 rounded text-[11px] font-medium bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                        >
                          {vc}
                        </span>
                      ))}
                      {member.voice_categories.length > 6 && (
                        <span className="px-2 py-0.5 rounded text-[11px] text-slate-400 bg-slate-700/40">
                          +{member.voice_categories.length - 6}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-1 rounded text-xs inline-block ${member.is_available !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {member.is_available !== false ? 'Disponível' : 'Indisponível'}
                    </span>
                    {member.unavailable_from && member.unavailable_to && formatUnavailabilityBadge(member.unavailable_from, member.unavailable_to) && (
                      <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded" data-testid={`unavail-${member.id}`}>
                        {formatUnavailabilityBadge(member.unavailable_from, member.unavailable_to)}
                      </span>
                    )}
                  </div>
                </div>
                {member.voice_sample_url && (
                  <button onClick={(e) => { e.stopPropagation(); togglePlay(member.id, member.voice_sample_url); }}
                    className={`mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
                      playingId === member.id ? 'bg-teal-500 text-white' : 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                    }`} data-testid={`play-sample-${member.id}`}>
                    {playingId === member.id ? <><Pause className="h-4 w-4" />Parar</> : <><Play className="h-4 w-4" />Ouvir Amostra</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nenhum membro encontrado.</p>
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={closeUserModal}>
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-4 mb-6">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt="" className="w-20 h-20 rounded-full" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 text-3xl font-bold">
                    {selectedUser.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedUser.artist_name || selectedUser.name}</h2>
                  {isAdmin && selectedUser.artist_name && <p className="text-slate-400">{selectedUser.name}</p>}
                  <span className={`px-3 py-1 rounded text-sm capitalize mt-2 inline-block ${
                    selectedUser.role === 'dono' ? 'bg-amber-500/20 text-amber-400' :
                    selectedUser.role === 'administrador' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-teal-500/20 text-teal-400'
                  }`}>{selectedUser.role}</span>
                </div>
              </div>
              <div className="space-y-4">
                {selectedUser.bio && <div><label className="text-slate-400 text-sm">Bio</label><p className="text-white mt-1 text-sm bg-slate-700/30 px-3 py-2 rounded">{selectedUser.bio}</p></div>}
                <div className="grid grid-cols-2 gap-4">
                  {selectedUser.birth_date && <div><label className="text-slate-400 text-sm">Idade</label><p className="text-white">{calculateAge(selectedUser.birth_date)} anos</p></div>}
                  {selectedUser.voice_type && <div><label className="text-slate-400 text-sm">Tipo de Voz</label><p className="text-white capitalize">{selectedUser.voice_type}</p></div>}
                  {selectedUser.equipment && <div className="col-span-2"><label className="text-slate-400 text-sm">Equipamento</label><p className="text-white">{selectedUser.equipment}</p></div>}
                  {selectedUser.drt && <div className="col-span-2"><label className="text-slate-400 text-sm">DRT (Registro de Ator)</label><p className="text-white font-mono bg-slate-700/50 px-3 py-1.5 rounded">{selectedUser.drt}</p></div>}
                  {isAdmin && selectedUser.pix_key && (
                    <div className="col-span-2">
                      <label className="text-slate-400 text-sm">Chave PIX</label>
                      <div className="flex gap-2 mt-1">
                        <p className="text-white font-mono bg-slate-700/50 px-3 py-1.5 rounded flex-1">{selectedUser.pix_key}</p>
                        {selectedUser.pix_bank && <p className="text-teal-400 bg-teal-500/10 px-3 py-1.5 rounded text-sm">{selectedUser.pix_bank}</p>}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-slate-400 text-sm">Disponibilidade</label>
                  <div className={`px-3 py-2 rounded mt-1 ${selectedUser.is_available !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {selectedUser.is_available !== false ? 'Disponível' : 'Indisponível'}
                  </div>
                  {selectedUser.unavailable_from && selectedUser.unavailable_to && formatUnavailabilityBadge(selectedUser.unavailable_from, selectedUser.unavailable_to) && (
                    <div className="mt-2 px-3 py-2 rounded bg-amber-500/10 text-amber-400 text-sm" data-testid="modal-unavail">
                      {formatUnavailabilityBadge(selectedUser.unavailable_from, selectedUser.unavailable_to)}
                    </div>
                  )}
                </div>
                {/* Emite NF - visible to admin */}
                {isAdmin && (
                  <div>
                    <label className="text-slate-400 text-sm">Nota Fiscal</label>
                    <div className={`px-3 py-2 rounded mt-1 ${selectedUser.emite_nf ? 'bg-teal-500/20 text-teal-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {selectedUser.emite_nf ? 'Emite Nota Fiscal' : 'Não emite Nota Fiscal'}
                    </div>
                  </div>
                )}
                {/* Rating Details - visible to self and admin */}
                {notasGerais[selectedUser.id] !== undefined && (isAdmin || selectedUser.id === user?.id) && (
                  <RatingDetails userId={selectedUser.id} isAdmin={isAdmin} isSelf={selectedUser.id === user?.id} getAuthHeaders={getAuthHeaders} />
                )}
                {selectedUser.voice_sample_url && (
                  <div className="flex gap-2">
                    <button onClick={() => togglePlay(selectedUser.id, selectedUser.voice_sample_url)}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
                        playingId === selectedUser.id ? 'bg-teal-500 text-white' : 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                      }`}>
                      {playingId === selectedUser.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      {playingId === selectedUser.id ? 'Parar' : 'Ouvir Amostra'}
                    </button>
                    <button onClick={() => handleDownloadVoiceSample(selectedUser.voice_sample_url, selectedUser.artist_name || selectedUser.name)}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 px-4 py-2 rounded-lg transition-colors">
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <button onClick={closeUserModal} className="w-full mt-6 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors">Fechar</button>
            </div>
          </div>
        )}

        {/* Calendar Modal */}
        <CalendarModal
          open={showCalendar}
          onClose={() => setShowCalendar(false)}
          calendarEvents={calendarEvents}
          calMonth={calMonth}
          calYear={calYear}
          setCalMonth={setCalMonth}
          setCalYear={setCalYear}
        />
      </div>
    </Layout>
  );
};

export default Community;
